/**
 * 중앙화된 API 클라이언트
 * 모든 API 호출을 단일 인스턴스로 관리하여 일관성과 유지보수성 향상
 */

import axios from "axios";
import { getBaseUrl } from "../config/env.js";
import { handleApiError } from "../utils/errorHandler.js";

/**
 * axios 인스턴스 생성
 */
const apiClient = axios.create({
  baseURL: getBaseUrl(),
  timeout: 30000, // 30초 타임아웃
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * 요청 인터셉터
 * 모든 요청 전에 실행되는 로직
 */
apiClient.interceptors.request.use(
  (config) => {
    // FormData인 경우 Content-Type 헤더를 제거하여 axios가 자동으로 multipart/form-data 설정
    // axios 1.x에서는 FormData일 때 Content-Type을 명시적으로 제거해야 함
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    // 개발 모드에서 요청 로깅
    if (process.env.NODE_ENV === "development") {
      console.log(
        "API Request:",
        config.method.toUpperCase(),
        config.url,
        config.params || config.data
      );
    }

    // 향후 확장: 인증 토큰 자동 추가
    // const token = storage.getToken();
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }

    return config;
  },
  (error) => {
    // 요청 설정 오류
    if (process.env.NODE_ENV === "development") {
      console.error("API Request Error:", error);
    }
    return Promise.reject(error);
  }
);

/**
 * 응답 인터셉터
 * 모든 응답 후에 실행되는 로직
 */
apiClient.interceptors.response.use(
  (response) => {
    // 개발 모드에서 응답 로깅
    if (process.env.NODE_ENV === "development") {
      console.log("API Response:", response.config.url, response.data);
    }

    return response;
  },
  (error) => {
    // 기본 에러 처리 (개별 요청에서 skipErrorHandler가 false인 경우)
    if (!error.config?.skipErrorHandler) {
      handleApiError(error);
    }
    return Promise.reject(error);
  }
);

/**
 * 공통 API 메서드
 * 모든 API 호출은 이 메서드들을 통해 수행
 */
export const api = {
  /**
   * GET 요청
   * @param {string} endpoint - API 엔드포인트
   * @param {Object} params - 쿼리 파라미터
   * @param {Object} options - 추가 옵션 (skipErrorHandler 등)
   * @returns {Promise} API 응답 데이터
   */
  get: async (endpoint, params = {}, options = {}) => {
    try {
      // skipErrorHandler를 분리하여 axios config에 전달
      const { skipErrorHandler, ...axiosConfig } = options;
      
      // params가 undefined이거나 null인 경우 빈 객체로 처리
      const requestParams = params || {};
      
      const config = {
        params: requestParams,
        ...axiosConfig,
      };
      
      // skipErrorHandler를 config에 명시적으로 추가
      if (skipErrorHandler) {
        config.skipErrorHandler = true;
      }
      
      // 개발 모드에서 파라미터 확인
      if (process.env.NODE_ENV === "development") {
        console.log("api.get called:", { endpoint, params: requestParams, config });
      }
      
      const response = await apiClient.get(endpoint, config);
      return response.data;
    } catch (error) {
      // 에러는 인터셉터에서 처리되지만, 추가 처리가 필요한 경우
      throw error;
    }
  },

  /**
   * POST 요청
   * @param {string} endpoint - API 엔드포인트
   * @param {Object|FormData} data - 요청 본문 데이터
   * @param {Object} options - 추가 옵션 (headers, skipErrorHandler 등)
   * @returns {Promise} API 응답 데이터
   */
  post: async (endpoint, data = {}, options = {}) => {
    try {
      const config = {
        ...options,
      };

      // FormData가 아닌 경우에만 Content-Type 설정
      // FormData인 경우는 인터셉터에서 처리됨
      if (!(data instanceof FormData)) {
        config.headers = {
          "Content-Type": "application/json",
          ...options.headers,
        };
      }

      const response = await apiClient.post(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 향후 확장: PUT, DELETE 메서드
  // put: async (endpoint, data = {}, options = {}) => { ... },
  // delete: async (endpoint, params = {}, options = {}) => { ... },
};

/**
 * axios 인스턴스를 직접 사용해야 하는 경우를 위한 export
 */
// api 객체를 기본 export로도 제공 (편의성)
export default api;

